import { Grid } from '@mui/material';
import { Formik, Form, type FormikProps } from 'formik';
import { useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import Alert from '../../Alert';
import Button from '../../Buttons/Button';

import { FIELD_COMPONENTS } from './fieldComponents';

import type { FormikFormProps, FormikFormField } from '@/types/formTypes';

const FormikForm = ({
  formRef = null,
  fields,
  initialValues,
  validationSchema = null,
  onSubmit,
  onCancel = null,
  submitLabel,
  cancelLabel,
  autoFocus = true,
  formErrorName,
}: FormikFormProps): ReactNode => {
  const intl = useIntl();
  const resolvedSubmitLabel = submitLabel ?? intl.formatMessage({ id: 'submit' });
  const resolvedCancelLabel = cancelLabel ?? intl.formatMessage({ id: 'cancel' });
  const optionalSuffix = intl.formatMessage({ id: 'optionalFieldSuffix' });

  useEffect(() => {
    if (autoFocus) {
      const firstField = fields.find(field => !field.disabled);
      if (firstField) {
        requestAnimationFrame(() => {
          const element: HTMLInputElement | null = document.querySelector(
            `input[name="${firstField.name}"]`
          );
          element?.focus();
        });
      }
    }
  }, [autoFocus, fields]);

  const isFieldRequired = useCallback(
    (fieldName: string) => {
      const fieldSchema = validationSchema?.fields[fieldName];
      if (fieldSchema && 'tests' in fieldSchema && Array.isArray(fieldSchema.tests)) {
        return fieldSchema.tests.some(
          (test: { OPTIONS?: { name?: string } }) => test.OPTIONS?.name === 'required'
        );
      }
      return false;
    },
    [validationSchema]
  );

  const processedFields = useMemo(
    () =>
      fields.map(field => {
        if (typeof field.label === 'string' && validationSchema) {
          const required = isFieldRequired(field.name);
          return {
            ...field,
            label: required ? `${field.label} *` : `${field.label} ${optionalSuffix}`,
          };
        }
        return field;
      }),
    [fields, validationSchema, isFieldRequired, optionalSuffix]
  );

  const renderField = (field: FormikFormField): ReactNode => {
    const { fieldType, fieldContent, ...fieldProps } = field;
    const FieldComponent = FIELD_COMPONENTS[fieldType];

    if (!FieldComponent) {
      console.error(`FieldComponent for fieldType "${fieldType}" is not defined.`);
      return null;
    }
    return (
      <FieldComponent
        {...(fieldProps as Record<string, unknown>)}
        name={field.name}
        label={field.label ?? ''}
      >
        {fieldContent}
      </FieldComponent>
    );
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef as React.RefObject<FormikProps<Record<string, unknown>>> | null}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isValid, dirty, errors }) => {
        const formError = formErrorName ? errors[formErrorName] : null;

        return (
          <Form>
            <Grid
              container
              direction='column'
            >
              {processedFields.map((field, index) => (
                <Grid
                  item
                  key={field.name || `field-${String(index)}`}
                  style={{
                    ...(index === 1 ? {} : { marginTop: '5px' }),
                    ...(field.style ?? {}),
                  }}
                >
                  {renderField(field)}
                </Grid>
              ))}
              {typeof formError === 'string' && (
                <Grid
                  item
                  mt={1}
                >
                  <Alert
                    mode='error'
                    title={formError}
                    text=''
                  />
                </Grid>
              )}
              <Grid
                container
                justifyContent='end'
                gap={1}
                mt={1}
              >
                {onCancel && (
                  <Grid item>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={onCancel}
                      label={resolvedCancelLabel}
                    />
                  </Grid>
                )}
                <Grid item>
                  <Button
                    type='submit'
                    label={resolvedSubmitLabel}
                    disabled={!isValid || !dirty}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default FormikForm;
